import { GoogleOutlined } from '@ant-design/icons';
import { Button, Form, Input, message } from 'antd';
import { getAdditionalUserInfo } from 'firebase/auth';
import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getAndClearIntendedUrl, hasIntendedUrl } from '../../utils/persistentNavigation';
import { getAndClearPlanIntent } from '../../utils/planCarry';
import { createCheckout } from '../../services/billingApi';
import { SEO_DEFAULTS, PAGE_SEO } from '../../utils/seoConfig';
import { CellCogEvents } from '../../utils/analytics';
import { BRAND_MARK_ON_DARK, BRAND_MARK_ON_LIGHT } from '../../utils/brandMark';
import { getAuthErrorNotice, isSilentAuthError } from './authErrorCopy';
import { isMcpAuthorizePath, pathOf, trackMcpStep } from '../../utils/mcpConnectFunnel';
import './AuthForm.css';

const Login = () => {
  // Separate loading states so clicking Google doesn't spin the email button too.
  const [emailLoading, setEmailLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  // Inline persistent error notice (replaces the auto-dismissing toast).
  const [errNotice, setErrNotice] = useState(null);
  const [form] = Form.useForm();
  const { login, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  // Check if this is an Electron app login flow
  const isElectronLogin = searchParams.get('electron') === 'true';
  // Email carried over from /signup ("already have an account" flow) — no retyping.
  const prefillEmail = searchParams.get('email') || undefined;

  const emailInputRef = useRef(null);
  const passwordInputRef = useRef(null);

  // Autofocus (desktop only): phones would pop the keyboard and hide the page.
  // If the email came prefilled from the other page, jump straight to password.
  // CEL-1115: the programmatic focus must NOT count as form engagement — its
  // focus event fires synchronously inside .focus(), so the suppress flag set
  // around the call swallows exactly that one event (cleared on the next
  // tick). Genuine engagement on the already-focused field is caught by
  // typing (onValuesChange) instead.
  const suppressAutofocusRef = useRef(false);
  useEffect(() => {
    const isDesktop =
      typeof window !== 'undefined' &&
      window.matchMedia('(min-width: 769px) and (hover: hover)').matches;
    if (!isDesktop) return;
    suppressAutofocusRef.current = true;
    if (prefillEmail) {
      passwordInputRef.current?.focus();
    } else {
      emailInputRef.current?.focus();
    }
    setTimeout(() => {
      suppressAutofocusRef.current = false;
    }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // auth_form_start: fires once per page view, on first engagement (field focus
  // or Google click). Gives us the true "tried to log in" denominator.
  const formStartedRef = useRef(false);
  const markFormStart = (method) => {
    if (formStartedRef.current || suppressAutofocusRef.current) return;
    formStartedRef.current = true;
    CellCogEvents.authFormStart('login', method);
  };

  const handleSuccessfulLogin = async (isNewUser = false) => {
    // If this is an Electron app login, redirect with token instead of navigating
    if (isElectronLogin) {
      try {
        const { auth: firebaseAuth } = await import('../../firebase');
        const user = firebaseAuth.currentUser;
        if (user) {
          const token = await user.getIdToken();
          const email = user.email || '';
          // Pass refresh token for persistent auth in Electron app
          const refreshToken = user.stsTokenManager?.refreshToken || '';
          // Redirect to callback page that Electron app captures
          window.location.href = `/auth/electron-callback?token=${encodeURIComponent(token)}&email=${encodeURIComponent(email)}&refreshToken=${encodeURIComponent(refreshToken)}`;
          return;
        }
      } catch (err) {
        console.error('Failed to get token for Electron:', err);
      }
    }

    message.success('Logged in successfully');

    // Plan carry-through (CEL-1234): a plan clicked on /pricing lands on
    // /signup, whose "Log In" link brings existing accounts here. EXISTING
    // accounts consume the stash now and go straight to checkout. NEW accounts
    // (Google on /login silently creates them) leave it for /welcome — they
    // still have consent ahead of them, and checkout belongs after it.
    if (!isNewUser) {
      const intent = getAndClearPlanIntent();
      if (intent) {
        try {
          const response = await createCheckout(intent.plan, intent.interval, '/?billing_success=true', 'login_carried');
          if (response.data?.checkout_url) {
            window.location.href = response.data.checkout_url;
            return;
          }
        } catch (err) {
          console.error('Plan carry checkout failed:', err); // fall through to normal navigation
        }
      }
    }

    // CEL-3365: a pending MCP consent request (/authorize) beats a stale stored destination.
    const mcpReturn = pathOf(location.state?.from);
    if (isMcpAuthorizePath(mcpReturn)) {
      getAndClearIntendedUrl();
      navigate(mcpReturn, { replace: true });
      return;
    }
    if (hasIntendedUrl()) {
      const intendedUrl = getAndClearIntendedUrl();
      navigate(intendedUrl);
      return;
    }
    // A cold deep link (a task or billing link from an email) never sets the intended URL: ProtectedRoute
    // saved it on location.state instead. AuthPage navigates there too, but this navigate('/') ran last
    // and won the race, so a signed-out click on a task link landed on Home (Nitish, Sep 11 2026).
    const from = location.state?.from;
    navigate(from?.pathname ? `${from.pathname}${from.search || ''}${from.hash || ''}` : '/', { replace: true });
  };

  const onFinish = async (values) => {
    setEmailLoading(true);
    setErrNotice(null);
    // Set flag before auth so GlobalLayout can detect it when onAuthStateChanged fires
    sessionStorage.setItem('cellcog_just_authenticated', 'true');
    try {
      await login(values.email, values.password);

      CellCogEvents.login('email');

      handleSuccessfulLogin();
    } catch (error) {
      sessionStorage.removeItem('cellcog_just_authenticated');
      CellCogEvents.authError('login', 'email', error?.code || 'unknown');
      setErrNotice(getAuthErrorNotice(error, 'login', values.email));
    }
    setEmailLoading(false);
  };

  const handleGoogleLogin = async () => {
    markFormStart('google');
    CellCogEvents.authSubmit('login', 'google');
    setGoogleLoading(true);
    setErrNotice(null);
    // Set flag before auth so GlobalLayout can detect it when onAuthStateChanged fires
    sessionStorage.setItem('cellcog_just_authenticated', 'true');
    try {
      const result = await loginWithGoogle('login');
      // Popup blocked -> we've been redirected to Google (CEL-1086); completion
      // (session cookie + analytics) happens in AuthContext when we return.
      if (!result) return;

      // Google on /login silently CREATES an account for brand-new users.
      // Count it as the signup it really is, so sign_up/login stay honest
      // in both directions (see TRACKING.md).
      const isNewUser = getAdditionalUserInfo(result)?.isNewUser;
      if (isNewUser) {
        CellCogEvents.signUp('google', { source_page: 'login', is_new_user: true });
        if (isMcpAuthorizePath(pathOf(location.state?.from))) trackMcpStep('account_created', { method: 'google' });
      } else {
        CellCogEvents.login('google');
      }

      handleSuccessfulLogin(isNewUser === true);
    } catch (error) {
      sessionStorage.removeItem('cellcog_just_authenticated');
      // Closing the Google popup is a choice, not a failure — no error UI, no event.
      if (!isSilentAuthError(error)) {
        CellCogEvents.authError('login', 'google', error?.code || 'unknown');
        setErrNotice(getAuthErrorNotice(error, 'login'));
      }
    }
    setGoogleLoading(false);
  };

  // "Reward early, punish late" (validation model, Nitish Aug 24):
  // - FORMAT rules (valid email) validate on blur, and only for non-empty
  //   values — the user clearly attempted the field.
  // - REQUIRED/empty rules validate ONLY on submit (rule-level
  //   validateTrigger: 'onSubmit' keeps them out of the blur pass): clicking
  //   elsewhere, or clicking "Continue with Google", must never mark an
  //   untouched field red. OAuth is a separate pathway from the email form.
  // - Once a field HAS an error it re-validates on every keystroke so the
  //   error clears the instant it's fixed. Editing also clears the inline notice.
  const onValuesChange = (changedValues) => {
    // Typing is genuine engagement — needed because the autofocused field's
    // focus event is suppressed (CEL-1115); a desktop user who just starts
    // typing would otherwise never fire auth_form_start.
    markFormStart('email');
    if (errNotice) setErrNotice(null);
    const errored = Object.keys(changedValues).filter(
      (name) => form.getFieldError(name).length > 0
    );
    if (errored.length) form.validateFields(errored);
  };

  return (
    <>
      <Helmet>
        <title>{PAGE_SEO.login.title} | {SEO_DEFAULTS.siteName}</title>
        <meta name="description" content={PAGE_SEO.login.description} />
        <link rel="canonical" href={`${SEO_DEFAULTS.url}/login`} />
        <meta property="og:title" content={PAGE_SEO.login.title} />
        <meta property="og:description" content={PAGE_SEO.login.description} />
        <meta property="og:url" content={`${SEO_DEFAULTS.url}/login`} />
        <meta name="twitter:title" content={PAGE_SEO.login.title} />
        <meta name="twitter:description" content={PAGE_SEO.login.description} />
      </Helmet>

      <div className="auth-page-container">
        <div className="auth-form-wrapper">
          <div className="auth-form-card">
            <div className="auth-form-header">
              <div className="auth-form-logo">
                {/* Two marks, one visible per face (the mkt-face pattern from
                    MarketingAdaptiveTheme.css): html[data-mkt-theme] is set
                    PRE-PAINT by the boot script, so the right mark shows on
                    the prerendered page before React resolves the theme — a
                    JS-picked src would flash the dark-face (white) mark on a
                    light-OS first paint (CEL-1627). */}
                <img
                  className="mkt-face-dark"
                  src={BRAND_MARK_ON_DARK}
                  alt="CellCog Logo"
                  style={{
                    width: '64px',
                    height: '64px',
                    objectFit: 'contain',
                  }}
                />
                <img
                  className="mkt-face-light"
                  src={BRAND_MARK_ON_LIGHT}
                  alt="CellCog Logo"
                  style={{
                    width: '64px',
                    height: '64px',
                    objectFit: 'contain',
                  }}
                />
              </div>
              <h1 className="auth-form-title">Welcome back</h1>
              <p className="auth-form-subtitle">Log in to your account to continue</p>
              <p className="auth-form-credits-note">New here? 200 free credits · no card required</p>
            </div>

            {/* Google first: returning Google users are one click from done. */}
            <Button
              onClick={handleGoogleLogin}
              loading={googleLoading}
              icon={<GoogleOutlined />}
              block
              size="large"
              className="auth-google-btn"
            >
              Continue with Google
            </Button>

            <div className="auth-or-divider">or</div>

            <Form
              form={form}
              name="login"
              onFinish={onFinish}
              onSubmitCapture={() => CellCogEvents.authSubmit('login', 'email')}
              onValuesChange={onValuesChange}
              layout="vertical"
              className="auth-form"
              requiredMark={false}
              validateTrigger="onBlur"
              scrollToFirstError
              initialValues={{ email: prefillEmail }}
            >
              <Form.Item
                name="email"
                rules={[
                  // Required: submit-only (see the validation comment above).
                  { required: true, message: 'Please enter your email', validateTrigger: 'onSubmit' },
                  // Format: blur; async-validator skips empty values for
                  // non-required rules, so an untouched field stays quiet.
                  { type: 'email', message: 'Please enter a valid email', validateTrigger: 'onBlur' }
                ]}
              >
                {/* inputMode (not type="email"): gives phones the @ keyboard
                    without triggering the browser's native validation bubbles,
                    which would compete with antd's inline errors. Enter on the
                    email field jumps to password instead of submitting half a
                    form (enterKeyHint makes the key say "next"/"go"). */}
                <Input
                  ref={emailInputRef}
                  placeholder="Email address"
                  size="large"
                  autoComplete="email"
                  inputMode="email"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  enterKeyHint="next"
                  onPressEnter={(e) => {
                    e.preventDefault();
                    passwordInputRef.current?.focus();
                  }}
                  onFocus={() => markFormStart('email')}
                />
              </Form.Item>

              <Form.Item
                name="password"
                rules={[
                  { required: true, message: 'Please enter your password', validateTrigger: 'onSubmit' }
                ]}
              >
                <Input.Password
                  ref={passwordInputRef}
                  placeholder="Password"
                  size="large"
                  autoComplete="current-password"
                  enterKeyHint="go"
                  onFocus={() => markFormStart('email')}
                />
              </Form.Item>

              {errNotice && (
                <div className="auth-inline-error" role="alert">
                  <span>{errNotice.text}</span>
                  {errNotice.links.length > 0 && (
                    <span className="auth-inline-error-links">
                      {errNotice.links.map((l) =>
                        l.action === 'google' ? (
                          // One-click escape hatch at the moment of failure
                          // (CEL-1115): fires the same Google flow as the main
                          // button, which also clears this notice.
                          <button
                            key={l.label}
                            type="button"
                            className="auth-inline-error-action"
                            onClick={handleGoogleLogin}
                          >
                            {l.label}
                          </button>
                        ) : (
                          <Link key={l.to} to={l.to} state={location.state}>{l.label}</Link>
                        )
                      )}
                    </span>
                  )}
                </div>
              )}

              <div className="auth-form-buttons">
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={emailLoading}
                  block
                  size="large"
                >
                  Log In
                </Button>
              </div>

              <div className="auth-form-secondary-links">
                <Link to="/forgot-password" className="auth-form-secondary-link">
                  Forgot password?
                </Link>
              </div>
            </Form>

            <div className="auth-form-links">
              <span className="auth-form-link">Don't have an account?</span>
              <Link to="/signup" state={location.state} className="auth-form-link auth-form-link-primary">
                Sign Up
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;
